import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core'
import { ActivatedRoute, ParamMap, Router } from '@angular/router'
import { BehaviorSubject, fromEvent, Observable, of, Subscription } from 'rxjs'
import { catchError, exhaustMap, filter, switchMap, take, takeUntil, tap } from 'rxjs/operators'
import { NgScrollbar } from 'ngx-scrollbar'

import { DestroyService } from '@core/services/destroy.service'
import { MessengerService } from '@app/modules/client/messenger/services/messenger.service'
import { ChatSocketService } from '@app/modules/client/messenger/services/chat-socket.service'
import { User } from '@shared/models/user.model'
import { AuthService } from '@app/modules/auth/auth.service'
import {
  ChatMessageModel,
  ChatModel,
  ChatSocketEventEnum,
  ChatSocketResponse,
  ChatUser,
  ChatUserModel,
} from '@shared/models/messenger/messenger.model'
import { MessengerApiService } from '../../services/messenger-api.service'
import { ChatMessageListComponent } from '@app/modules/client/messenger/components/chat-messages-list/chat-message-list.component'
import { PaginationData } from '@shared/models/pages-data.model'
import { cloneDeep } from 'lodash-es'
import { sameId } from '@core/helpers/same-id'

@Component({
  selector: 'lin-chat-details',
  templateUrl: './chat-details.component.html',
  styleUrls: ['./chat-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
  standalone: false,
})
export class ChatDetailsComponent implements OnInit, AfterViewInit {
  chatId: number
  currentUser: User
  selectedChat$: Observable<ChatModel>
  chatMessageList$: Observable<ChatMessageModel[]>
  typing = new BehaviorSubject<ChatUserModel | null>(null)
  isScrollBottom$ = new BehaviorSubject<boolean>(false)
  isAttachment = false

  @ViewChild(ChatMessageListComponent, { static: false }) linChatMessageList: ChatMessageListComponent

  private chatMessageList: ChatMessageModel[] = []
  private selectedChat: ChatModel
  private page = 1
  private limit = 20
  private total: number
  private measureScrollOffsetBottom = 0
  private activeChannelEditSubscription: Subscription

  get perfectScroll(): NgScrollbar {
    return this.linChatMessageList.perfectScrollbar
  }

  get perfectScrollNativeElement(): HTMLElement {
    return this.perfectScroll.nativeElement
  }

  get isGroupChat(): boolean {
    return !this.selectedChat?.isIndividual
  }

  get lastInterlocutorMessage(): ChatMessageModel | null {
    const filterInterlocutorMessage = (message: ChatMessageModel): boolean =>
      !sameId(message?.user?.id, this.currentUser.id) && !message?.isRead
    const interlocutorChatMessageList = this.chatMessageList.filter(filterInterlocutorMessage)

    return interlocutorChatMessageList?.[interlocutorChatMessageList?.length - 1] || null
  }

  get firstInterlocutorChatMessage(): ChatMessageModel | null {
    const filterInterlocutorMessage = (message: ChatMessageModel): boolean =>
      !sameId(message?.user?.id, this.currentUser.id) && !message?.isRead
    const interlocutorChatMessageList = this.chatMessageList.filter(filterInterlocutorMessage)

    return interlocutorChatMessageList?.[0] || null
  }

  get isMyChat(): boolean {
    const isMe = (user: ChatUserModel): boolean => sameId(user.id, this.currentUser?.id)
    return !!this.selectedChat?.users?.find(isMe)
  }

  get isScrollBottom(): boolean {
    const { scrollTop, clientHeight, scrollHeight } = this.perfectScrollNativeElement

    return scrollTop === scrollHeight - clientHeight
  }

  get unreadCurrentChatCount(): number | string | null {
    const count = this.chatMessageList?.filter(({ isRead, isReadByOther }: ChatMessageModel) => !isRead && !isReadByOther)?.length

    if (count) {
      return count < 10 ? count : '...'
    }

    return null
  }

  constructor(
    public readonly messengerService: MessengerService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly destroyService: DestroyService,
    private readonly messengerApiService: MessengerApiService,
    private readonly chatSocketService: ChatSocketService,
    private readonly authService: AuthService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initCurrentUser()
    this.initChatId()
    this.subscribeScrollToBottom()
    this.subscribeSelectedChat()
    this.subscribeChatMessageList()
    this.subscribeRouterCurrentChatId()
    this.subscribePrivateChannel()
  }

  ngAfterViewInit(): void {
    this.subscribeOnScrollChanged()
    this.subscribeReadChatMessageList()
    this.isScrollBottom$.next(this.isScrollBottom)
  }

  handleTyping(typing: boolean): void {
    // Typing is best-effort: skip when there is no socket and swallow a denied publish
    this.selectedChat &&
      this.chatSocketService.centrifuge &&
      this.chatSocketService
        .sendMessage(this.selectedChat.channel, {
          event: ChatSocketEventEnum.EVENT_MESSAGE_TYPING,
          data: {
            typing: typing,
            chat: this.selectedChat,
          },
          user: new ChatUser({
            networkStatus: 1,
            id: this.currentUser.id,
            email: this.currentUser.email,
            name: this.currentUser.name,
            surname: this.currentUser.surname,
            role: this.currentUser.role,
            phone: this.currentUser.phone,
            status: this.currentUser.status,
            lastLoggedAt: this.currentUser.lastLoggedAt,
            picture: this.currentUser.picture,
          }),
        })
        .pipe(
          catchError(() => of(null)),
          takeUntil(this.destroyService),
        )
        .subscribe()
  }

  private initCurrentUser(): void {
    this.currentUser = this.authService.getCurrentUser()
  }

  private initChatId(): void {
    const currentRouteId = this.activatedRoute.snapshot.paramMap.get('id')
    if (currentRouteId && Number(currentRouteId)) {
      this.chatId = Number(currentRouteId)
    }
  }

  private subscribeSelectedChat(): void {
    const selectedChatNext = (selectedChat: ChatModel): ChatModel => (this.selectedChat = selectedChat)
    this.selectedChat$ = this.messengerService.selectedChat$
    this.messengerService.selectedChat$.pipe(takeUntil(this.destroyService)).subscribe(selectedChatNext)
  }

  private subscribeChatMessageList(): void {
    this.chatMessageList$ = this.messengerService.chatMessageList$
    const chatMessageListNext = (chatMessageList: ChatMessageModel[]): ChatMessageModel[] => {
      this.chatMessageList = chatMessageList

      return chatMessageList
    }

    this.messengerService.chatMessageList$.pipe(takeUntil(this.destroyService)).subscribe(chatMessageListNext)
  }

  private subscribeRouterCurrentChatId(): void {
    const currentChatIdSubscribe = (paramMap: ParamMap): Observable<number> => {
      const id = paramMap.get('id')
      this.chatId = id && Number(id)
      this.total = 0
      this.page = 1
      this.measureScrollOffsetBottom = 0
      this.messengerService.setChatMessageList([])
      this.typing.next(null)

      this.cdr.detectChanges()
      return of(this.chatId)
    }

    const setCurrentChartId = (): void => {
      this.messengerService.setSelectedChatId(this.chatId || null)
    }

    const getCurrentChat = (): Observable<ChatModel> => this.messengerApiService.getChatById(this.chatId)

    const readFirstMessage = (): Observable<ChatMessageModel | unknown> => {
      return this.firstInterlocutorChatMessage && !this.firstInterlocutorChatMessage.isRead
        ? this.messengerApiService.patchReadMessage(this.chatId, this.firstInterlocutorChatMessage.id)
        : of(null)
    }

    this.activatedRoute.paramMap
      .pipe(
        takeUntil(this.destroyService),
        tap(() => this.messengerService.setLoading(true)),
        switchMap(currentChatIdSubscribe),
        tap(setCurrentChartId),
        switchMap(getCurrentChat),
        tap(this.setCurrentChat),
        switchMap(this.getChatHistory),
        tap(this.messageListSubscribe),
        switchMap(readFirstMessage),
        switchMap(() => this.chatSocketService.stateCentrifuge$),
        switchMap(() => this.connectActiveChannel()),
      )
      .subscribe({ next: this.channelActiveSubscribe, error: () => void this.router.navigate([...this.messengerBaseCommands]) })
  }

  private connectActiveChannel = (reset?: boolean): Observable<ChatSocketResponse> => {
    return this.chatSocketService.connectActiveChannel(this.selectedChat.channel, reset)
  }

  private channelActiveSubscribe = (response: ChatSocketResponse): void => {
    const {
      data: {
        data: { typing, chat },
        user,
      },
    } = response

    if (!user && typing && chat) {
      this.typing.next(null)
      return
    }

    // console.log('ChatDetailsComponent channelActiveSubscribe response', response)

    const isTyping = response.data.event === ChatSocketEventEnum.EVENT_MESSAGE_TYPING
    const isOtherUser = !sameId(this.currentUser.id, user?.id)
    const isCurrentChat = sameId(chat?.id, this.chatId)

    if (isTyping && isCurrentChat && isOtherUser && typing) {
      this.typing.next(user)
    } else {
      this.typing.next(null)
    }
  }

  private subscribePrivateChannel(): void {
    const connectPrivateChannelSubscribe = (data: ChatSocketResponse): void => {
      this.privateChannelActions(data)
    }

    this.chatSocketService.stateCentrifuge$
      .pipe(switchMap(this.chatSocketService.connectPrivateChannel), takeUntil(this.destroyService))
      .subscribe(connectPrivateChannelSubscribe)
  }

  private privateChannelActions(response: ChatSocketResponse): void {
    const {
      data: {
        event,
        data: { message, chat },
      },
    } = response

    switch (event) {
      case ChatSocketEventEnum.EVENT_MESSAGE_SENT: {
        this.handleSocketEventMessageSent(message)

        break
      }

      case ChatSocketEventEnum.EVENT_MESSAGE_READ: {
        this.handleSocketEventMessageRead(chat, message)

        break
      }

      case ChatSocketEventEnum.EVENT_MESSAGE_EDITED: {
        this.handleSocketEventMessageEdit(message)

        break
      }

      case ChatSocketEventEnum.EVENT_MESSAGE_DELETED: {
        this.handleSocketEventMessageDelete(message)

        break
      }

      case ChatSocketEventEnum.EVENT_CHAT_EDITED: {
        this.handleSocketEventChatEdit(chat)

        break
      }

      case ChatSocketEventEnum.EVENT_USERS_DELETED_FROM_CHAT:
      case ChatSocketEventEnum.EVENT_USERS_ADDED_TO_CHAT: {
        this.handleSocketEventJoinLeaveUser(chat)

        break
      }
      default: {
        return
      }
    }

    this.cdr.markForCheck()
  }

  private handleSocketEventMessageSent = (message: ChatMessageModel): void => {
    if (!sameId(this.chatId, message?.chat?.id)) {
      return
    }

    this.messengerService.setChatMessageList([...this.chatMessageList, message])
    this.total = this.total + 1

    if (this.isScrollBottom || sameId(message.user.id, this.currentUser.id)) {
      this.scrollToBottom()
    }

    if (this.isNoScroll()) {
      this.readChatMessageLastMessage()
    }
  }

  private handleSocketEventMessageRead = (chat: { id: number }, message: { id: number }): void => {
    if (!sameId(this.chatId, chat?.id)) {
      return
    }

    const findIndexChatMessage = (chatMessage: ChatMessageModel): boolean => sameId(chatMessage.id, message.id)
    const updateIndexChatMessage = this.chatMessageList.findIndex(findIndexChatMessage)

    if (updateIndexChatMessage < 0) {
      return
    }

    this.chatMessageList[updateIndexChatMessage].isRead = true
    this.chatMessageList[updateIndexChatMessage].isReadByOther = true
    this.chatMessageList[updateIndexChatMessage] = cloneDeep(this.chatMessageList[updateIndexChatMessage])

    this.messengerService.setChatMessageList(this.chatMessageList.slice())
  }

  private handleSocketEventMessageEdit = (message: ChatMessageModel): void => {
    if (!sameId(this.chatId, message?.chat?.id)) {
      return
    }

    const findIndexChatMessage = (chatMessage: ChatMessageModel): boolean => sameId(chatMessage.id, message.id)
    const updateIndexChatMessage = this.chatMessageList.findIndex(findIndexChatMessage)

    if (updateIndexChatMessage < 0) {
      return
    }

    if (sameId(this.chatMessageList[updateIndexChatMessage].id, message.id)) {
      this.chatMessageList[updateIndexChatMessage] = message
    }

    if (this.isNoScroll()) {
      this.readChatMessageLastMessage()
    }

    this.messengerService.setChatMessageList(this.chatMessageList.slice())
  }

  private handleSocketEventMessageDelete = (message: ChatMessageModel): void => {
    if (!sameId(this.chatId, message?.chat?.id)) {
      return
    }
    const findIndexChatMessage = (chatMessage: ChatMessageModel): boolean => sameId(chatMessage.id, message.id)
    const updateIndexChatMessage = this.chatMessageList.findIndex(findIndexChatMessage)

    if (updateIndexChatMessage < 0) {
      return
    }
    this.chatMessageList[updateIndexChatMessage] = message
    this.messengerService.setChatMessageList(this.chatMessageList.slice())
  }

  private handleSocketEventChatEdit = (chat: ChatModel): void => {
    if (!sameId(this.chatId, chat?.id)) {
      return
    }

    if (chat.users.length > 1) {
      this.messengerService.setSelectedChat(chat)
      this.activeChannelEditSubscription?.unsubscribe()
      this.activeChannelEditSubscription = this.connectActiveChannel(true).subscribe(this.channelActiveSubscribe)
    }
  }

  // Route base for "return to the messenger root" navigation (chat failed to load, or the current
  // user was removed from the open chat). v1 stays under /client/messenger; ChatDetailsV2Component
  // overrides this so v2 users are not ejected into the v1 app.
  protected messengerBaseCommands: (string | number)[] = ['client', 'messenger']

  private handleSocketEventJoinLeaveUser = (chat: ChatModel): void => {
    const isCurrentChat = sameId(chat?.id, this.selectedChat?.id)
    const findMe = (user: ChatUserModel): boolean => sameId(user.id, this.currentUser.id)
    const isInUsers = chat.users.find(findMe)

    if (isCurrentChat && !(this.isMyChat && !isInUsers) && chat.users.length > 1) {
      this.messengerService.setSelectedChat(chat)
    } else {
      void this.router.navigate([...this.messengerBaseCommands])
    }
  }

  private readLastMessage = (): Observable<ChatMessageModel | unknown> => {
    return this.lastInterlocutorMessage && !this.lastInterlocutorMessage?.isRead
      ? this.messengerApiService.patchReadMessage(this.chatId, this.lastInterlocutorMessage.id)
      : of(null)
  }

  private subscribeOnScrollChanged(): void {
    const isScrollNearTop = (event: { target: HTMLElement }): boolean => {
      const { scrollTop } = event.target

      return scrollTop === 0 && this.total > this.chatMessageList.length
    }

    const isScrollNearBottom = (): boolean => {
      const isLastUnread = !this.lastInterlocutorMessage?.isRead

      return this.isScrollBottom && isLastUnread
    }

    const getMessageList = (): Observable<PaginationData<ChatMessageModel[]>> => {
      this.page = this.page + 1
      return this.messengerApiService.getMessageList(this.chatId, { limit: this.limit, page: this.page })
    }

    const getMessageListSubscribe = ({ data: chatMessageList, total }: PaginationData<ChatMessageModel[]>): void => {
      this.total = total
      const prevScrollHeight = this.perfectScrollNativeElement.scrollHeight
      this.messengerService.setChatMessageList([...chatMessageList, ...this.chatMessageList])
      this.cdr.detectChanges()

      setTimeout(() => {
        const nextScrollHeight = this.perfectScrollNativeElement.scrollHeight
        void this.perfectScroll.scrollTo({ top: nextScrollHeight - prevScrollHeight })
      }, 0)
    }

    fromEvent(this.perfectScroll.nativeElement, 'scroll')
      .pipe(
        filter(() => {
          const scrollElement = this.perfectScroll.nativeElement
          return scrollElement.scrollTop === 0 && this.total > this.chatMessageList.length
        }),
        exhaustMap(getMessageList),
        takeUntil(this.destroyService),
      )
      .subscribe(getMessageListSubscribe)

    fromEvent(this.perfectScroll.nativeElement, 'scroll')
      .pipe(
        filter(() => isScrollNearBottom()),
        switchMap(() => this.readLastMessage()),
        filter(Boolean),
        takeUntil(this.destroyService),
      )
      .subscribe()

    fromEvent(this.perfectScroll.nativeElement, 'scroll')
      .pipe(takeUntil(this.destroyService))
      .subscribe(() => {
        this.isScrollBottom$.next(this.isScrollBottom)
        this.cdr.detectChanges()
      })
  }

  private subscribeReadChatMessageList(): void {
    const elemInViewPort = (elem: HTMLElement): boolean => {
      const rect = elem.getBoundingClientRect()
      const containerBottom = this.perfectScrollNativeElement.getBoundingClientRect().bottom

      const topShown = rect.top >= 0
      const bottomShown = rect.bottom - 5 <= containerBottom
      return topShown && bottomShown
    }

    const findElement = (): Observable<ChatMessageModel> => {
      const unreadChatMessageList = this.chatMessageList.filter(Boolean).filter(({ isRead }: ChatMessageModel) => !isRead)

      const hiedUnreadElement = unreadChatMessageList?.find((message) => {
        const elem = document.getElementById('item-' + message?.id.toString())
        if (!elem) return false

        return elemInViewPort(elem)
      })

      return of(hiedUnreadElement)
    }

    const patchReadMessage = (message: ChatMessageModel): Observable<ChatMessageModel> => {
      return this.messengerApiService.patchReadMessage(this.chatId, message.id)
    }

    fromEvent(this.perfectScroll.nativeElement, 'scroll')
      .pipe(switchMap(findElement), filter(Boolean), switchMap(patchReadMessage), takeUntil(this.destroyService))
      .subscribe()
  }

  private scrollToBottom = (): void => {
    setTimeout(() => {
      void this.perfectScroll.scrollTo({ bottom: 0 })
    }, 0)
  }

  private setCurrentChat = (chat: ChatModel): void => {
    this.messengerService.setLoading(false)
    this.messengerService.setSelectedChat(chat)
  }

  private getChatHistory = (): Observable<PaginationData<ChatMessageModel[]>> => {
    const limit = this.selectedChat.unreadCount >= this.limit / 2 ? this.selectedChat.unreadCount + this.limit : this.limit

    return this.messengerApiService.getMessageList(this.chatId, { limit })
  }

  private messageListSubscribe = ({ data, total }: PaginationData<ChatMessageModel[]>): void => {
    this.total = total
    this.messengerService.setChatMessageList(data)

    if (data.length && !this.selectedChat.unreadCount) {
      this.scrollToBottom()
    }

    if (this.firstInterlocutorChatMessage) {
      setTimeout(() => {
        const element = document.getElementById(`item-${this.firstInterlocutorChatMessage.id}`)

        void this.perfectScroll.scrollTo({ top: element.getBoundingClientRect().y - this.perfectScrollNativeElement.clientHeight })
      }, 0)
    }
  }

  private subscribeScrollToBottom(): void {
    this.messengerService.scrollToBottom$.pipe(takeUntil(this.destroyService)).subscribe(this.scrollToBottom)
  }

  private readChatMessageLastMessage = (): void => {
    this.readLastMessage().pipe(take(1), takeUntil(this.destroyService)).subscribe()
  }

  private isNoScroll(): boolean {
    const { scrollTop, clientHeight, scrollHeight } = this.perfectScrollNativeElement

    return scrollTop === 0 && scrollHeight - clientHeight === 0
  }
}
